import { calculate } from '@deizi/pricing';
import {
  Channel,
  type ChannelList,
  type Settings,
  formatBRL,
  pricingCalculateExample,
} from '@deizi/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { z } from 'zod';
import { AffixInput, Field, Toggle } from '../../components/Form';
import { Alert } from '../../components/States';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { bpsInput, parseBps, parseCentavos } from '../../lib/numbers';
import { StepHead, WizardFooter } from './WizardParts';

interface Suggestion {
  name: string;
  color: string;
  on: boolean;
  sub: string;
  commission: string;
  fixedFee: string;
  /** Fixed fee only below this price (ML); empty = every sale. */
  fixedBelow: string;
  shippingAvg: string;
  shippingFrom: string;
  payment: string;
  paymentMethods?: { name: string; feeBps: number | null }[];
}

/** Example fees from the design boards, to be checked against the marketplaces. */
const SUGGESTIONS: Suggestion[] = [
  {
    name: 'ML Clássico',
    color: '#E8C547',
    on: true,
    sub: 'A partir de R$ 79 o frete grátis é seu',
    commission: '14',
    fixedFee: '6,25',
    fixedBelow: '79,00',
    shippingAvg: '22,00',
    shippingFrom: '79,00',
    payment: '0',
  },
  {
    name: 'ML Premium',
    color: '#C9A227',
    on: false,
    sub: 'Mesmo frete do Clássico; anúncio com parcelamento sem juros',
    commission: '19',
    fixedFee: '6,25',
    fixedBelow: '79,00',
    shippingAvg: '22,00',
    shippingFrom: '79,00',
    payment: '0',
  },
  {
    name: 'Shopee',
    color: '#EE4D2D',
    on: false,
    sub: 'Comissão e tarifa por item vendido',
    commission: '20',
    fixedFee: '7,00',
    fixedBelow: '',
    shippingAvg: '0',
    shippingFrom: '',
    payment: '0',
  },
  {
    name: 'Direto · Pix',
    color: '#0F5D6B',
    on: true,
    sub: 'WhatsApp, Instagram e encomendas',
    commission: '0',
    fixedFee: '0',
    fixedBelow: '',
    shippingAvg: '0',
    shippingFrom: '',
    payment: '0',
    paymentMethods: [
      { name: 'Pix', feeBps: 0 },
      { name: 'Crédito à vista', feeBps: 499 },
      { name: 'Débito', feeBps: 199 },
    ],
  },
  {
    name: 'Feira',
    color: '#7FB2BA',
    on: false,
    sub: 'Pix e dinheiro sem taxa; maquininha no cartão',
    commission: '0',
    fixedFee: '0',
    fixedBelow: '',
    shippingAvg: '0',
    shippingFrom: '',
    payment: '2,99',
  },
];

function firstVersion(s: Suggestion) {
  const fee = parseCentavos(s.fixedFee) ?? 0;
  const below = parseCentavos(s.fixedBelow);
  const shippingAverageCentavos = parseCentavos(s.shippingAvg) ?? 0;
  return {
    commissionBps: parseBps(s.commission) ?? 0,
    paymentFeeBps: parseBps(s.payment) ?? 0,
    fixedFees:
      fee > 0
        ? [
            {
              fromCentavos: 0,
              toCentavos: below !== null && below > 0 ? below - 1 : null,
              feeCentavos: fee,
            },
          ]
        : [],
    shippingFromCentavos: shippingAverageCentavos > 0 ? parseCentavos(s.shippingFrom) : null,
    shippingAverageCentavos,
  };
}

function feeLine(s: Suggestion): string {
  const parts: string[] = [];
  const commission = parseBps(s.commission) ?? 0;
  if (commission > 0) parts.push(`${bpsInput(commission)}%`);
  const fee = parseCentavos(s.fixedFee) ?? 0;
  if (fee > 0)
    parts.push(
      `${formatBRL(fee)} ${s.fixedBelow ? `por venda abaixo de R$ ${s.fixedBelow}` : 'por item'}`,
    );
  const pay = parseBps(s.payment) ?? 0;
  if (pay > 0) parts.push(`${bpsInput(pay)}% na maquininha`);
  return parts.length > 0 ? parts.join(' + ') : 'Sem taxa';
}

/** Step 5 · Onde você vende. */
export function StepChannels({
  channels,
  settings,
  onBack,
  onNext,
}: {
  channels: z.infer<typeof ChannelList>['data'];
  settings: Settings;
  onBack: () => void;
  onNext: () => void;
}) {
  const qc = useQueryClient();
  const existingNames = new Set(channels.map((c) => c.name));
  const [rows, setRows] = useState(
    SUGGESTIONS.filter((s) => !existingNames.has(s.name)).map((s) => ({
      ...s,
      on: channels.length === 0 && s.on,
    })),
  );
  const [open, setOpen] = useState<string | null>(null);
  const selected = rows.filter((r) => r.on);
  const set = (name: string, patch: Partial<Suggestion>) =>
    setRows(rows.map((r) => (r.name === name ? { ...r, ...patch } : r)));

  const create = useMutation({
    mutationFn: async (toCreate: Suggestion[]) => {
      for (const s of toCreate) {
        await api.post('/channels', Channel, {
          name: s.name,
          color: s.color,
          paymentMethods: s.paymentMethods ?? [],
          firstVersion: firstVersion(s),
        });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      void qc.invalidateQueries({ queryKey: ['setup'] });
      onNext();
    },
  });

  // Vaso Ondas M price on each channel with the margins from step 2.
  const rates = settings.costs;
  const preview = (s: Suggestion) => {
    const result = calculate({
      ...pricingCalculateExample,
      rates: {
        ...pricingCalculateExample.rates,
        targetMarginBps: rates.targetMarginBps ?? 2500,
        minimumMarginBps: rates.minimumMarginBps ?? 1500,
        taxBps: rates.taxMode === 'percent' ? (rates.taxBps ?? 0) : 0,
      },
      rounding: settings.rules.rounding,
      channels: [
        {
          id: s.name,
          name: s.name,
          ...firstVersion(s),
          shipping:
            (parseCentavos(s.shippingAvg) ?? 0) > 0
              ? {
                  fromCentavos: parseCentavos(s.shippingFrom),
                  averageCentavos: parseCentavos(s.shippingAvg) ?? 0,
                }
              : null,
        },
      ],
    });
    return result.channels[0]?.suggested?.priceCentavos ?? null;
  };

  return (
    <>
      <StepHead title="Onde você vende">
        Marque os canais por onde começa. As tarifas vêm preenchidas; confira e ajuste em Canais
        quando quiser.
      </StepHead>
      <Alert tone="warn">
        <b>Tarifas de exemplo.</b> Confira no Mercado Livre e na Shopee antes de usar: elas mudam
        com frequência e por categoria.
      </Alert>
      {channels.length > 0 ? (
        <section className="card col" style={{ gap: 4 }}>
          <h3>Já cadastrados</h3>
          {channels.map((c) => (
            <div key={c.id} className="kv">
              <span className="row" style={{ gap: 8 }}>
                <span className="dot" style={{ background: c.color }} />
                {c.name}
              </span>
              <span className="v sm muted">{c.active ? 'ativo' : 'inativo'}</span>
            </div>
          ))}
        </section>
      ) : null}
      <section className="card" style={{ padding: '4px 18px' }}>
        {rows.map((s) => {
          const price = preview(s);
          return (
            <div
              key={s.name}
              className="col"
              style={{ borderBottom: '1px solid var(--line)', padding: '12px 0', gap: 10 }}
            >
              <div className="row wrap" style={{ gap: 12 }}>
                <Toggle
                  on={s.on}
                  onChange={(v) => set(s.name, { on: v })}
                  label={`Vender em ${s.name}`}
                />
                <span className="dot" style={{ background: s.color }} />
                <div className="grow" style={{ minWidth: 0 }}>
                  <p style={{ fontWeight: 500 }}>
                    {s.name} <span className="sm muted">· {feeLine(s)}</span>
                  </p>
                  <p className="xs muted">{s.sub}</p>
                </div>
                <span className="sm muted">
                  Vaso Ondas M: {price === null ? '—' : <b className="tab">{formatBRL(price)}</b>}
                </span>
                <button
                  type="button"
                  className="btn sm g"
                  onClick={() => setOpen(open === s.name ? null : s.name)}
                  aria-expanded={open === s.name}
                >
                  Ajustar tarifas
                </button>
              </div>
              {open === s.name ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit,minmax(min(150px,100%),1fr))',
                    gap: 10,
                  }}
                >
                  <Field label="Comissão">
                    <AffixInput
                      ariaLabel={`Comissão de ${s.name}`}
                      unit="%"
                      value={s.commission}
                      onChange={(v) => set(s.name, { commission: v })}
                    />
                  </Field>
                  <Field label="Tarifa fixa">
                    <AffixInput
                      ariaLabel={`Tarifa fixa de ${s.name}`}
                      prefix="R$"
                      value={s.fixedFee}
                      onChange={(v) => set(s.name, { fixedFee: v })}
                    />
                  </Field>
                  <Field label="Só abaixo de" help="Vazio: toda venda.">
                    <AffixInput
                      ariaLabel={`Tarifa só abaixo de, ${s.name}`}
                      prefix="R$"
                      value={s.fixedBelow}
                      onChange={(v) => set(s.name, { fixedBelow: v })}
                    />
                  </Field>
                  <Field label="Frete pago por você" help="Média. 0 = o comprador paga.">
                    <AffixInput
                      ariaLabel={`Frete de ${s.name}`}
                      prefix="R$"
                      value={s.shippingAvg}
                      onChange={(v) => set(s.name, { shippingAvg: v })}
                    />
                  </Field>
                  <Field label="Frete a partir de" help="Vazio: sempre.">
                    <AffixInput
                      ariaLabel={`Frete a partir de, ${s.name}`}
                      prefix="R$"
                      value={s.shippingFrom}
                      onChange={(v) => set(s.name, { shippingFrom: v })}
                    />
                  </Field>
                  <Field label="Taxa de pagamento">
                    <AffixInput
                      ariaLabel={`Taxa de pagamento de ${s.name}`}
                      unit="%"
                      value={s.payment}
                      onChange={(v) => set(s.name, { payment: v })}
                    />
                  </Field>
                </div>
              ) : null}
            </div>
          );
        })}
        {rows.length === 0 ? (
          <p className="sm muted" style={{ padding: '12px 0' }}>
            Todos os canais sugeridos já estão cadastrados.
          </p>
        ) : null}
      </section>
      <p className="xs muted">
        {selected.length} {selected.length === 1 ? 'canal selecionado' : 'canais selecionados'} ·
        Preços do Vaso Ondas M com a margem alvo do passo 2.
      </p>
      {create.isError ? <Alert tone="err">{errorMessage(create.error)}</Alert> : null}
      <WizardFooter
        busy={create.isPending}
        blockMsg={
          channels.length === 0 && selected.length === 0
            ? 'Escolha pelo menos um canal de venda.'
            : null
        }
        onBack={onBack}
        onContinue={() => create.mutate(selected)}
      />
    </>
  );
}
